var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { createComponent } from '@workday/canvas-kit-react/common';
// type changes no need
import { type } from '@workday/canvas-kit-react/tokens';
import { Box } from '@workday/canvas-kit-react/layout';
/**
 * Function returns updated props by token and variant values
*/
var validateProps = function (_a) {
    var typeLevel = _a.typeLevel, variant = _a.variant, props = __rest(_a, ["typeLevel", "variant"]);
    var updatedProps = props;
    var tokenPropNames = [
        'color',
        'fontFamily',
        'fontSize',
        'fontWeight',
        'letterSpacing',
        'lineHeight',
    ];
    /*
    If token provided it updates undefined values of token styles:
    `fontFamily`, `fontSize`, `lineHeight`, `fontWeight`, `color`
    by replacing them by token value
    */
    if (typeLevel) {
        var _b = typeLevel.split('.'), level = _b[0], size = _b[1];
        var tokenProps_1 = type.levels[level][size];
        tokenPropNames.forEach(function (item) {
            if (!props[item]) {
                updatedProps[item] = tokenProps_1[item];
            }
        });
    }
    // If variant provided it updates color value by variant color
    if (variant) {
        updatedProps = __assign(__assign({}, updatedProps), type.variants[variant]);
    }
    return updatedProps;
};
/**
 * ## Text
 * [View Docs](https://canvas.workday.com/components/text/text)
 *
 * ---
 * This is a generic base component intended to render any text.
 * It's built on top of the `Box` component, so it has access to all [BoxProps](https://canvas.workday.com/components/layout/box/#props).
 * It also has `typeLevel` and `variant` style props that simplify navigating our type hierarchy and using [Canvas type tokens](https://canvas.workday.com/tokens/type#type-styles).
 * By default, it renders a semantic `span` element, but you can adjust this as needed with the `as` prop.
 *
 * The type hierarchy is organized into four levels, which correspond to our [Canvas type levels](https://canvas.workday.com/tokens/type#type-styles).
 *
 * - `title`: Intended to be used for large page titles.
 * - `heading`: Intended to be used for headings and large text.
 * - `body`: Intended to be used for standard body text.
 * - `subtext`: Intended to be used for small subtext content or in tight spaces.
 *
 * Each level has three sizes: `large`, `medium`, and `small`.
 *
 * You can provide any level and size to the `typeLevel` prop, and it will apply the correct styles accordingly.
 * In the example below we're providing the `subtext` level and `small` size to the component with the value `subtext.small`.
 *
 * @example
 * ```tsx
 * import { Text } from '@workday/canvas-kit-react/text';
 *
 * const CustomErrorText = () => (
 *   <Text typeLevel="subtext.small" variant="error">
 *     Error Text
 *   </Text>
 * );
 * ```
*/
export var Text = createComponent('span')({
    displayName: 'Text',
    Component: function (_a, ref, Element) {
        var children = _a.children, elemProps = __rest(_a, ["children"]);
        return (React.createElement(Box, __assign({ ref: ref, as: Element }, validateProps(elemProps)), children));
    },
});
